const SORT_STATES = ['asc', 'desc']

export const getEnrollmentsColumns = (t) => [
  { label: t('enrollmentsBilling.columns.programName'), field: 'program_name' },
  { label: t('enrollmentsBilling.columns.enrollmentStatus'), field: 'enrollment_status' },
  { label: t('enrollmentsBilling.columns.billingStatus'), field: 'payment_status' },
  { label: t('enrollmentsBilling.columns.latestUpdate'), field: 'last_update' },
  { label: t('enrollmentsBilling.columns.amount'), field: 'amount' },
  { label: t('enrollmentsBilling.columns.paymentMethod'), field: 'payment_method' },
  { label: t('enrollmentsBilling.columns.package'), field: 'package' },
  { label: t('enrollmentsBilling.columns.age'), field: 'age' },
  { label: t('enrollmentsBilling.columns.level'), field: 'level' },
  { label: t('enrollmentsBilling.columns.gender'), field: 'gender' },
  // Backend has no date sort column, so this is display-only (non-sortable).
  { label: t('enrollmentsBilling.columns.programDates'), field: 'program_dates', sortable: false },
]

export const DEFAULT_SORT_OPTION = {
  sortField: 'last_update',
  sortValue: 'desc',
}

export const getSortStates = () => SORT_STATES

export const getEnrollmentStatusOptions = (t) => [
  { label: t('enrollmentsBilling.filters.enrollmentStatus.active'), value: 'active' },
  { label: t('enrollmentsBilling.filters.enrollmentStatus.future'), value: 'future' },
  { label: t('enrollmentsBilling.filters.enrollmentStatus.completed'), value: 'completed' },
  { label: t('enrollmentsBilling.filters.enrollmentStatus.dropped'), value: 'dropped' },
]

export const getPaymentStatusOptions = (t) => [
  { label: t('enrollmentsBilling.filters.paymentStatus.outstanding'), value: 'outstanding' },
  { label: t('enrollmentsBilling.filters.paymentStatus.completed'), value: 'paid' },
  { label: t('enrollmentsBilling.filters.paymentStatus.refunded'), value: 'refunded' },
  { label: t('enrollmentsBilling.filters.paymentStatus.partiallyRefunded'), value: 'partially_refunded' },
]

const ENROLLMENT_STATUS_KEYS = {
  active: 'active',
  future: 'future',
  completed: 'completed',
  dropped: 'dropped',
  pending_approval: 'pendingApproval',
  applied: 'pendingApproval',
  waitlisted: 'waitlisted',
  pending: 'pending',
}

export const enrollmentStatusLabel = (status, t) => (
  ENROLLMENT_STATUS_KEYS[status] ? t(`enrollmentsBilling.enrollmentStatus.${ENROLLMENT_STATUS_KEYS[status]}`) : status
)

export const formatInvoiceStatusDisplay = (invoice, t) => {
  if (!invoice) { return { label: t('enrollmentsBilling.billingStatus.noBilling'), variant: 'none' } }

  const { status, paymentMethod } = invoice
  const isOnline = paymentMethod === 'online'

  if (status === 'paid') {
    return isOnline
      ? { label: t('enrollmentsBilling.billingStatus.paidOnline'), variant: 'paid' }
      : { label: t('enrollmentsBilling.billingStatus.offlinePayment'), variant: 'offlinePayment' }
  }
  if (status === 'refunded') {
    return isOnline
      ? { label: t('enrollmentsBilling.billingStatus.refundedOnline'), variant: 'refunded' }
      : { label: t('enrollmentsBilling.billingStatus.refundedOffline'), variant: 'refunded' }
  }
  if (status === 'partially_refunded') {
    return { label: t('enrollmentsBilling.billingStatus.partialRefund'), variant: 'refunded' }
  }
  return { label: t(`enrollmentsBilling.billingStatus.${status}`, status), variant: status }
}

// Phase 1 billing status is driven by the enrollment's own payment_status (paid / paid_online /
// invoiced / outstanding) rather than a full invoice. Reuses the invoice badge variants for
// icon + color so no extra styling is needed.
export const formatPaymentStatusDisplay = (paymentStatus, t) => {
  switch (paymentStatus) {
    case 'paid_online':
      return { label: t('enrollmentsBilling.billingStatus.paidOnline'), variant: 'paid' }
    case 'paid':
      return { label: t('enrollmentsBilling.billingStatus.offlinePayment'), variant: 'offlinePayment' }
    case 'outstanding':
      return { label: t('enrollmentsBilling.billingStatus.outstanding'), variant: 'outstanding' }
    case 'invoiced':
      return { label: t('enrollmentsBilling.billingStatus.invoiced'), variant: 'invoiced' }
    // Only reachable for a refunded online payment — an offline refund always has an invoice, whose
    // own status drives the badge instead.
    case 'refunded':
      return { label: t('enrollmentsBilling.billingStatus.refundedOnline'), variant: 'refunded' }
    default:
      return { label: t('enrollmentsBilling.billingStatus.noBilling'), variant: 'none' }
  }
}

// --- Drawer state helpers ---

export const hasBillingTab = (enrollment) => !!enrollment?.invoice

// An online payment is permanent: such an enrollment is never deletable, even once it's been dropped
// and refunded. payment_status flips to 'refunded' at that point, so it can't be the only signal —
// fall back to how the charge was taken (the invoice's method, or the enrollment's card method).
// Scoped to 'refunded' so an offline refund isn't mistaken for an online one.
export const wasPaidOnline = (enrollment) => {
  if (enrollment?.paymentStatus === 'paid_online') { return true }
  if (enrollment?.paymentStatus !== 'refunded') { return false }

  return enrollment.invoice ? enrollment.invoice.paymentMethod === 'online' : enrollment.paymentMethod === 'card'
}

// Refund + offline-payment state is derived from the invoice's entries (payment/refund records)
// rather than denormalized invoice fields. Refund entries store negative amounts (Stripe ledger
// convention), so negate the sum to get the positive refunded magnitude.
export const invoiceRefundAmount = (invoice) => (
  -(invoice?.entries || [])
    .filter(entry => entry.invoiceType === 'refund')
    .reduce((sum, entry) => sum + Number(entry.amount || 0), 0)
)

export const invoiceOfflinePaymentDescription = (invoice) => {
  const payments = (invoice?.entries || []).filter(entry => entry.invoiceType === 'payment')
  return payments.length ? payments[payments.length - 1].description : null
}

const PAYMENT_METHOD_KEYS = {
  online: 'online',
  cash: 'cash',
  check: 'check',
  member_charge: 'memberCharge',
  custom: 'custom',
}

export const paymentMethodLabel = (method, t) => (
  PAYMENT_METHOD_KEYS[method] ? t(`enrollmentsBilling.paymentMethod.${PAYMENT_METHOD_KEYS[method]}`) : method
)

export const formatPaymentMethodDisplay = (invoice, t) => {
  if (!invoice?.paymentMethod) { return null }
  const { paymentMethod } = invoice

  if (paymentMethod === 'online') { return t('enrollmentsBilling.paymentMethodDisplay.onlineStripe') }

  return t('enrollmentsBilling.paymentMethodDisplay.offline', { method: paymentMethodLabel(paymentMethod, t) })
}

const ACTIVITY_KEYS = {
  created: 'created',
  approved: 'approved',
  package_changed: 'packageChanged',
  payment_status_changed: 'paymentStatusChanged',
  course_changed: 'courseChanged',
  completed: 'completed',
  refunded: 'refunded',
  deleted: 'deleted',
  transferred: 'transferred',
  dropped: 'dropped',
  invoice_created: 'invoiceCreated',
  invoice_edited: 'invoiceEdited',
  payment_reminder_sent: 'paymentReminderSent',
  payment_received: 'paymentReceived',
  marked_as_paid: 'markedAsPaid',
  marked_as_outstanding: 'markedAsOutstanding',
  refund_issued: 'refundIssued',
}

export const activityLabel = (type, t) => (
  ACTIVITY_KEYS[type] ? t(`enrollmentsBilling.activity.${ACTIVITY_KEYS[type]}`) : type
)

export const DISCOUNT_TYPES = { fixed: 'fixed', percent: 'percent' }

// A percentage is a way of entering a discount, not of storing it: the invoice keeps a fixed amount
// so the total can't drift if the package price changes later. Rounded to cents, and capped at 100%
// so the charge can't invert.
export const resolveDiscount = ({ discountType, discountAmount }, packagePrice) => {
  const entered = Number(discountAmount || 0)
  if (entered <= 0) { return 0 }
  if (discountType !== DISCOUNT_TYPES.percent) { return entered }

  return Math.round(Number(packagePrice || 0) * Math.min(entered, 100)) / 100
}

const DISCOUNT_PERCENT_PATTERN = /\((\d+(?:\.\d+)?)%\)/

// Reopening a saved invoice restores how the discount was entered, by reading the percentage back
// out of the description. Without this the form shows a fixed amount while the description still
// claims a percentage - the toggle and the label contradicting each other.
export const parseDiscountEntry = (discountItem) => {
  if (!discountItem) { return { discountType: DISCOUNT_TYPES.fixed, discountAmount: '' } }

  const percent = DISCOUNT_PERCENT_PATTERN.exec(discountItem.description || '')
  if (percent) { return { discountType: DISCOUNT_TYPES.percent, discountAmount: percent[1] } }

  return { discountType: DISCOUNT_TYPES.fixed, discountAmount: String(Math.abs(Number(discountItem.amount))) }
}

// The line item's description is the only place a percentage survives, so it carries it: the
// family's invoice and emails read "Discount (10%)" rather than a bare amount.
export const discountDescriptionFor = ({ discountType, discountAmount }) => {
  const entered = Number(discountAmount || 0)
  if (discountType !== DISCOUNT_TYPES.percent || entered <= 0) { return '' }

  return `Discount (${Math.min(entered, 100)}%)`
}
const REFUNDABLE_INVOICE_STATUSES = ['paid', 'partially_refunded']

export const needsDropRefund = (enrollment) => {
  if (enrollment?.paymentStatus === 'paid_online') { return true }

  const invoice = enrollment?.invoice
  if (!invoice) { return false }

  return REFUNDABLE_INVOICE_STATUSES.includes(invoice.status) || enrollment?.paymentStatus === 'paid'
}

export const dropModalFor = (enrollment) => {
  if (!needsDropRefund(enrollment)) { return 'drop' }

  return enrollment?.invoice ? 'dropRefund' : 'dropRefundLegacy'
}
